import { Button } from "@/components/ui/button.tsx";
import { Input } from "@/components/ui/input.tsx";
import { Label } from "@/components/ui/label.tsx";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover.tsx";
import { Separator } from "@/components/ui/separator.tsx";
import { Textarea } from "@/components/ui/textarea.tsx";
import { Toggle } from "@/components/ui/toggle.tsx";
import { ContactInput } from "@/components/ui/contact-input.tsx";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog.tsx";
import { confirmationToast, errorToast, infoToast } from "@/components/ui/toast.tsx";
import { artifactQueries } from "@/hooks/artifactQueries.ts";
import { useCurrentUserContext } from "@/hooks/useUser.tsx";
import { api, apiErrorMessage } from "@/lib/http.ts";
import { cn } from "@/lib/utils.ts";
import { shareSubjectLabel, tidyDomainInput } from "@/shared/artifactSharing.ts";
import type {
  ArtifactDomainShare,
  ArtifactRecipient,
  RevokeArtifactAccessResp,
  SetAppletStateSharedReq,
  SetAppletStateSharedResp,
  ShareArtifactReq,
  ShareArtifactResp,
  ShareArtifactWithDomainReq,
  ShareArtifactWithDomainResp,
  ShareArtifactWithEmailReq,
  ShareArtifactWithEmailResp,
} from "@/types/sharedArtifact.ts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Building2, Link2, X } from "lucide-react";
import { useId, useState } from "react";

export { canShareArtifact, isShareableArtifactType } from "@/shared/artifactSharing.ts";

/** A person the owner picked but has not shared with yet. */
type PendingRecipient = { email: string; name: string | null };

/**
 * Share one artifact — with named people, with everyone at a domain, and with
 * anyone holding a link.
 *
 * They are stacked but kept visibly separate, and that is the point rather than
 * layout: "shared with Alex", "anyone at acme.com" and "anyone with this link"
 * are different promises, and a UI that blurs them makes the access list a lie.
 * Removing someone therefore also has to say when another of them still reaches
 * them.
 */
export const ArtifactShareControl = ({
  artifactId,
  artifactType,
  title,
  triggerClassName,
}: {
  artifactId: string;
  artifactType: string;
  title?: string | null;
  /**
   * Sizing for the trigger, for a host whose row fixes the height of what sits
   * in it. Left off, the trigger keeps the standalone height it uses wherever
   * it stands on its own.
   */
  triggerClassName?: string;
}) => {
  const queryClient = useQueryClient();
  const toggleId = useId();
  const editToggleId = useId();
  const domainInputId = useId();
  const [isOpen, setIsOpen] = useState(false);
  const [pending, setPending] = useState<PendingRecipient | null>(null);
  // Null until the owner types, so the box offers their own domain until then.
  const [domainInput, setDomainInput] = useState<string | null>(null);
  const [pendingDomain, setPendingDomain] = useState<string | null>(null);

  const { data: share, isLoading } = useQuery(artifactQueries.shareState(artifactId));
  const { data: access } = useQuery(artifactQueries.access(artifactId));
  const { userContext } = useCurrentUserContext();

  const isApplet = artifactType === "applet";
  const recipients = access?.recipients ?? [];
  const domains = access?.domains ?? [];
  const isShared = Boolean(share) || recipients.length > 0 || domains.length > 0;

  const ownDomain = userContext?.user.hd?.toLowerCase() ?? null;
  const suggestedDomain =
    ownDomain !== null && !domains.some((share) => share.domain === ownDomain) ? ownDomain : "";
  const domainValue = domainInput ?? suggestedDomain;

  const invalidate = () => {
    void queryClient.invalidateQueries({
      queryKey: artifactQueries.shareState(artifactId).queryKey,
    });
    void queryClient.invalidateQueries({ queryKey: artifactQueries.access(artifactId).queryKey });
  };

  const mint = useMutation({
    mutationFn: async (body: ShareArtifactReq) => {
      const response = await api.post<ShareArtifactResp, ShareArtifactReq>(
        `/api/a/artifacts/${artifactId}/share`,
        body,
      );
      return response.data;
    },
    onSuccess: invalidate,
    // The server refuses a mint from a superseded version (409) — a tab opened
    // while this version was still the head. Its wording says how to recover, so
    // surface it rather than the generic failure.
    onError: (error) => errorToast(apiErrorMessage(error) ?? "Couldn't create the link."),
  });

  const revoke = useMutation({
    mutationFn: async () => api.delete(`/api/a/artifacts/${artifactId}/share`),
    onSuccess: () => {
      invalidate();
    },
    onError: () => errorToast("Couldn't turn the link off."),
  });

  const setAppletState = useMutation({
    mutationFn: async (body: SetAppletStateSharedReq) =>
      api.put<SetAppletStateSharedResp, SetAppletStateSharedReq>(
        `/api/a/artifacts/${artifactId}/appletState`,
        body,
      ),
    onSuccess: invalidate,
    onError: () => errorToast("Couldn't change the data setting."),
  });

  const removeRecipient = useMutation({
    mutationFn: async (email: string) => {
      // axios sends a DELETE body through `config.data`, not a second argument.
      const response = await api.delete<RevokeArtifactAccessResp>(
        `/api/a/artifacts/${artifactId}/access`,
        { data: { email } },
      );
      return response.data;
    },
    onSuccess: () => {
      invalidate();
    },
    onError: () => errorToast("Couldn't remove access."),
  });

  const removeDomain = useMutation({
    mutationFn: async (domain: string) => {
      const response = await api.delete<RevokeArtifactAccessResp>(
        `/api/a/artifacts/${artifactId}/access/domain`,
        { data: { domain } },
      );
      return response.data;
    },
    onSuccess: () => {
      invalidate();
    },
    onError: () => errorToast("Couldn't remove access."),
  });

  const copy = async (url: string) => {
    await navigator.clipboard.writeText(url);
    confirmationToast("Link copied");
  };

  const pickRecipient = (recipient: PendingRecipient) => {
    // Radix's popover and dialog fight over outside-click and focus if the
    // dialog is opened from inside the popover, so hand off rather than nest.
    setIsOpen(false);
    setPending(recipient);
  };

  const pickDomain = () => {
    const domain = tidyDomainInput(domainValue);
    if (!domain) return;
    setIsOpen(false);
    setDomainInput(null);
    setPendingDomain(domain);
  };

  return (
    <>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            data-statsig-name="artifact-share-open"
            // Green pairing from `StatusBadgeBlock`; hover restated so the ghost
            // variant's own `hover:bg-accent` can't drop it back to neutral.
            className={cn(
              isShared && "bg-green-50 text-green-700 hover:bg-green-100 hover:text-green-700",
              triggerClassName,
            )}
          >
            <Link2 className="size-4" />
            {isShared ? "Shared" : "Share"}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-96 space-y-4">
          <div className="space-y-2">
            <Label className="text-sm font-medium">Share with a person</Label>
            <ContactInput
              buttonLabel="Add someone"
              buttonAriaLabel="Share with a person"
              placeholder="Name or email address…"
              statsigName="artifact-share-pick-contact"
              filterHandleTypes={["emailAddress"]}
              onSelectContact={(contact) => {
                const emails = contact.handles?.filter((h) => h.type === "emailAddress") ?? [];
                const email = (emails.find((h) => h.isPreferred) ?? emails[0])?.value;
                if (email) pickRecipient({ email, name: contact.name ?? null });
              }}
              onSelectHandle={(contact, handle) =>
                pickRecipient({ email: handle.value, name: contact.name ?? null })
              }
              onSelectEmail={(email) => pickRecipient({ email, name: null })}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor={domainInputId} className="text-sm font-medium">
              Share with everyone at a domain
            </Label>
            <form
              className="flex items-center gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                pickDomain();
              }}
            >
              <Input
                id={domainInputId}
                value={domainValue}
                placeholder={ownDomain ?? "example.com"}
                autoComplete="off"
                onChange={(e) => setDomainInput(e.target.value)}
              />
              <Button
                type="submit"
                size="sm"
                variant="secondary"
                data-statsig-name="artifact-share-pick-domain"
                disabled={domainValue.trim() === ""}
              >
                Add
              </Button>
            </form>
          </div>

          {(recipients.length > 0 || domains.length > 0) && (
            <div className="space-y-1">
              <Label className="text-sm font-medium">People with access</Label>
              {domains.map((share) => (
                <DomainRow
                  key={share.domain}
                  share={share}
                  disabled={removeDomain.isPending}
                  onRemove={() => removeDomain.mutate(share.domain)}
                />
              ))}
              {recipients.map((recipient) => (
                <RecipientRow
                  key={recipient.email}
                  recipient={recipient}
                  disabled={removeRecipient.isPending}
                  onRemove={() => removeRecipient.mutate(recipient.email)}
                />
              ))}
            </div>
          )}

          <Separator />

          <div className="space-y-3">
            <Label className="text-sm font-medium">Anyone with the link</Label>
            {isLoading ? (
              <p className="text-muted-foreground text-sm">Loading…</p>
            ) : share ? (
              <>
                <div className="flex items-center gap-2">
                  <code className="flex-1 truncate rounded bg-muted px-2 py-1 text-xs">
                    {share.shareUrl}
                  </code>
                  <Button
                    size="sm"
                    data-statsig-name="artifact-share-copy"
                    onClick={() => copy(share.shareUrl)}
                  >
                    Copy
                  </Button>
                </div>
                <div className="flex justify-between">
                  <Button
                    variant="ghost"
                    size="sm"
                    data-statsig-name="artifact-share-rotate"
                    disabled={mint.isPending}
                    onClick={() => mint.mutate({ rotate: true })}
                  >
                    New link
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    data-statsig-name="artifact-share-revoke"
                    disabled={revoke.isPending}
                    onClick={() => revoke.mutate()}
                  >
                    Turn off
                  </Button>
                </div>
              </>
            ) : (
              <>
                <p className="text-muted-foreground text-sm">
                  Create a link anyone can open, without signing in.
                </p>
                <Button
                  size="sm"
                  data-statsig-name="artifact-share-create"
                  disabled={mint.isPending}
                  onClick={() => mint.mutate({})}
                >
                  Create link
                </Button>
              </>
            )}
          </div>

          {/* One setting for everyone the applet reaches, by name, by domain or
              by link: the consent lives on the saved data, which has no way to
              tell one viewer from another. Shown whenever it is shared at all,
              so an owner who only invited people by name can still see and
              change it. */}
          {isApplet && access !== undefined && isShared && (
            <div className="space-y-1">
              <Separator className="mb-3" />
              <div className="flex items-center justify-between gap-3">
                <Label htmlFor={toggleId} className="text-sm leading-snug">
                  Include current data
                </Label>
                <Toggle
                  id={toggleId}
                  checked={access.sharedWithViewers === true}
                  disabled={setAppletState.isPending}
                  onCheckedChange={(checked) =>
                    setAppletState.mutate({ sharedWithViewers: checked })
                  }
                />
              </div>
              {/* Three facts, none of which the user can recover from being
                  wrong about: the exposure is continuous rather than a
                  snapshot, the applet's own code can send that data anywhere
                  (its sandbox permits `connect-src https:` by design), and
                  unticking stops future loads but cannot retract what has
                  already been fetched. */}
              <p className="text-muted-foreground text-xs leading-snug">
                Everyone this applet is shared with sees its saved data as it is <em>right now</em>,
                including anything saved after you shared it — and the applet&apos;s own code can
                send that data anywhere. Unticking stops future viewers but can&apos;t take back
                what has already been seen.
              </p>
              {/* Only where viewers could write at all: an applet that saves
                  through the data API, with its data already shared. */}
              {access.sharedWithViewers === true && access.usesAppletData === true && (
                <div className="space-y-1 pt-3">
                  <div className="flex items-center justify-between gap-3">
                    <Label htmlFor={editToggleId} className="text-sm leading-snug">
                      Viewers can edit
                    </Label>
                    <Toggle
                      id={editToggleId}
                      checked={access.viewersCanEdit === true}
                      disabled={setAppletState.isPending}
                      onCheckedChange={(checked) =>
                        setAppletState.mutate({ sharedWithViewers: true, viewersCanEdit: checked })
                      }
                    />
                  </div>
                  <p className="text-muted-foreground text-xs leading-snug">
                    Everyone this applet is shared with can add, change and delete its data.
                  </p>
                </div>
              )}
            </div>
          )}
        </PopoverContent>
      </Popover>

      <ShareWithPersonDialog
        artifactId={artifactId}
        artifactType={artifactType}
        title={title}
        recipient={pending}
        includesAppletData={isApplet && access?.sharedWithViewers === true}
        onClose={() => setPending(null)}
        onShared={invalidate}
      />

      <ShareWithDomainDialog
        artifactId={artifactId}
        artifactType={artifactType}
        title={title}
        domain={pendingDomain}
        includesAppletData={isApplet && access?.sharedWithViewers === true}
        onClose={() => setPendingDomain(null)}
        onShared={invalidate}
      />
    </>
  );
};

/**
 * "Anyone at acme.com". No *Pending* badge, unlike a person: the grant is live
 * at once and resolves for each viewer who proves the domain.
 */
const DomainRow = ({
  share,
  disabled,
  onRemove,
}: {
  share: ArtifactDomainShare;
  disabled: boolean;
  onRemove: () => void;
}) => (
  <div className="flex items-center gap-2 rounded px-1 py-1 hover:bg-accent">
    <Building2 className="size-4 shrink-0 text-muted-foreground" />
    <p className="min-w-0 flex-1 truncate text-sm">Anyone at {share.domain}</p>
    <Button
      variant="ghost"
      size="sm"
      className="size-7 shrink-0 p-0"
      aria-label={`Stop sharing with ${share.domain}`}
      data-statsig-name="artifact-share-remove-domain"
      disabled={disabled}
      onClick={onRemove}
    >
      <X className="size-4" />
    </Button>
  </div>
);

const RecipientRow = ({
  recipient,
  disabled,
  onRemove,
}: {
  recipient: ArtifactRecipient;
  disabled: boolean;
  onRemove: () => void;
}) => (
  <div className="flex items-center gap-2 rounded px-1 py-1 hover:bg-accent">
    <div className="min-w-0 flex-1">
      <p className="truncate text-sm">{recipient.name ?? recipient.email}</p>
      {recipient.name && (
        <p className="truncate text-muted-foreground text-xs">{recipient.email}</p>
      )}
    </div>
    {/* Not "invited": the grant is already live and already revocable. It just
        cannot resolve to a person until one signs in with this address. */}
    {recipient.status === "pending" && (
      <span className="shrink-0 text-muted-foreground text-xs">Pending</span>
    )}
    <Button
      variant="ghost"
      size="sm"
      className="size-7 shrink-0 p-0"
      aria-label={`Remove ${recipient.email}`}
      data-statsig-name="artifact-share-remove-person"
      disabled={disabled}
      onClick={onRemove}
    >
      <X className="size-4" />
    </Button>
  </div>
);

/**
 * Confirm sharing with one person, and offer to tell them.
 *
 * A separate step rather than sharing straight off the typeahead: this is the
 * only place the owner is told the recipient must sign in as that address, and
 * — for an applet with its data switched on — that they are handing over its
 * saved contents.
 */
const ShareWithPersonDialog = ({
  artifactId,
  artifactType,
  title,
  recipient,
  includesAppletData,
  onClose,
  onShared,
}: {
  artifactId: string;
  artifactType: string;
  title?: string | null;
  recipient: PendingRecipient | null;
  includesAppletData: boolean;
  onClose: () => void;
  onShared: () => void;
}) => {
  const notifyId = useId();
  const [notify, setNotify] = useState(true);
  const [message, setMessage] = useState("");

  const close = () => {
    setNotify(true);
    setMessage("");
    onClose();
  };

  const share = useMutation({
    mutationFn: async (body: ShareArtifactWithEmailReq) => {
      const response = await api.post<ShareArtifactWithEmailResp, ShareArtifactWithEmailReq>(
        `/api/a/artifacts/${artifactId}/access`,
        body,
      );
      return response.data;
    },
    onSuccess: (result, variables) => {
      onShared();
      // The share landed, and the access list reports it, so there is nothing
      // to confirm. An email the user asked for and did not get is the
      // exception: nothing on the card records whether one went out, so staying
      // quiet would leave the owner believing the recipient had been told.
      //
      // Declining to notify returns the same `notificationSent: false`, and
      // that is the outcome they chose, so it says nothing. Compared against
      // `false` rather than `true` because `notify` is optional and the server
      // defaults it on: an omitted flag does send, and does warn.
      if (variables.notify !== false && !result.notificationSent) {
        const who = result.recipient.name ?? result.recipient.email;
        infoToast(`Shared with ${who}, but no email was sent`);
      }
      close();
    },
    onError: (error) => errorToast(apiErrorMessage(error) ?? "Couldn't share this."),
  });

  const label = shareSubjectLabel(artifactType, title);

  return (
    <Dialog
      open={recipient !== null}
      onOpenChange={(open) => {
        if (!open && !share.isPending) close();
      }}
    >
      <DialogContent
        className="sm:max-w-md"
        onEscapeKeyDown={(e) => {
          if (share.isPending) e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>Share {label}</DialogTitle>
          <DialogDescription>
            {recipient?.name ? `${recipient.name} (${recipient.email})` : recipient?.email} will be
            able to open it after signing in with that address.
          </DialogDescription>
        </DialogHeader>

        {includesAppletData && (
          <p className="text-muted-foreground text-sm leading-snug">
            This applet is set to include its saved data, so they will see it too.
          </p>
        )}

        <div className="flex items-center justify-between gap-3">
          <Label htmlFor={notifyId} className="text-sm">
            Send them an email
          </Label>
          <Toggle id={notifyId} checked={notify} onCheckedChange={setNotify} />
        </div>

        {notify && (
          <Textarea
            value={message}
            maxLength={1000}
            placeholder="Add a message (optional)"
            onChange={(e) => setMessage(e.target.value)}
          />
        )}

        <DialogFooter>
          <Button
            variant="ghost"
            data-statsig-name="artifact-share-cancel-person"
            onClick={close}
            disabled={share.isPending}
          >
            Cancel
          </Button>
          <Button
            data-statsig-name="artifact-share-confirm-person"
            disabled={share.isPending || recipient === null}
            onClick={() => {
              if (!recipient) return;
              share.mutate({
                email: recipient.email,
                notify,
                ...(notify && message.trim() ? { message: message.trim() } : {}),
              });
            }}
          >
            {share.isPending ? "Sharing…" : "Share"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Confirm sharing with everyone at a domain.
 *
 * Blunter than the person dialog, because this grant is standing and open-ended:
 * it reaches people who sign up later, none of them is told, and for an applet
 * with its data switched on it hands that data to all of them.
 */
const ShareWithDomainDialog = ({
  artifactId,
  artifactType,
  title,
  domain,
  includesAppletData,
  onClose,
  onShared,
}: {
  artifactId: string;
  artifactType: string;
  title?: string | null;
  domain: string | null;
  includesAppletData: boolean;
  onClose: () => void;
  onShared: () => void;
}) => {
  const share = useMutation({
    mutationFn: async (body: ShareArtifactWithDomainReq) => {
      const response = await api.post<ShareArtifactWithDomainResp, ShareArtifactWithDomainReq>(
        `/api/a/artifacts/${artifactId}/access/domain`,
        body,
      );
      return response.data;
    },
    onSuccess: () => {
      onShared();
      onClose();
    },
    // A consumer domain or a malformed one is refused with wording that says why.
    onError: (error) => errorToast(apiErrorMessage(error) ?? "Couldn't share this."),
  });

  const label = title?.trim() || (artifactType === "applet" ? "this applet" : "this document");

  return (
    <Dialog
      open={domain !== null}
      onOpenChange={(open) => {
        if (!open && !share.isPending) onClose();
      }}
    >
      <DialogContent
        className="sm:max-w-md"
        onEscapeKeyDown={(e) => {
          if (share.isPending) e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>
            Share {label} with everyone at {domain}
          </DialogTitle>
          <DialogDescription>
            Anyone who signs in with a {domain} Google Workspace or Microsoft 365 account will be
            able to open it — including people who join later. Nobody is emailed; it appears under
            Shared with me in their Library.
          </DialogDescription>
        </DialogHeader>

        {includesAppletData && (
          <p className="text-muted-foreground text-sm leading-snug">
            This applet is set to include its saved data, so everyone at {domain} will see it too.
          </p>
        )}

        <DialogFooter>
          <Button
            variant="ghost"
            data-statsig-name="artifact-share-cancel-domain"
            onClick={onClose}
            disabled={share.isPending}
          >
            Cancel
          </Button>
          <Button
            data-statsig-name="artifact-share-confirm-domain"
            disabled={share.isPending || domain === null}
            onClick={() => {
              if (domain) share.mutate({ domain });
            }}
          >
            {share.isPending ? "Sharing…" : "Share"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
